import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { workspaceApi } from '../services/api';
import SidebarHeader from '../components/SidebarHeader/SidebarHeader';
import LoadingSpinner from '../components/LoadingSpinner/LoadingSpinner';
import './WorkspaceList.css';

const ArticleDrafts = ({ onOpenLeftSidebar, onOpenRightSidebar }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);

  const author = user?.username || localStorage.getItem('hive_username');

  const load = useCallback(async () => {
    if (!author) return;
    setLoading(true);
    setError('');
    try {
      const data = await workspaceApi.listDrafts(author);
      setDrafts(data.drafts || []);
    } catch (err) {
      setError('Failed to load drafts.');
    } finally {
      setLoading(false);
    }
  }, [author]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this draft? This cannot be undone.')) return;
    setDeleting(id);
    try {
      await workspaceApi.deleteDraft(id, author);
      setDrafts(prev => prev.filter(d => d.id !== id));
    } catch {
      alert('Failed to delete draft.');
    } finally {
      setDeleting(null);
    }
  };

  if (!isAuthenticated) {
    return <div className="workspace-list-page"><p>Please log in.</p></div>;
  }

  return (
    <>
      <SidebarHeader onOpenLeftSidebar={onOpenLeftSidebar} onOpenRightSidebar={onOpenRightSidebar} />
      <div className="workspace-list-page">
        <div className="workspace-list-header">
          <h2>Drafts</h2>
          <Link to="/create-article" className="workspace-new-btn">+ New Article</Link>
        </div>

        {loading && <LoadingSpinner text="Loading drafts..." />}
        {error && <div className="workspace-error">{error}<button onClick={load}>Retry</button></div>}

        {!loading && !error && drafts.length === 0 && (
          <div className="workspace-empty">
            <p>No drafts yet.</p>
            <Link to="/create-article" className="workspace-new-btn">Start writing</Link>
          </div>
        )}

        {!loading && drafts.length > 0 && (
          <div className="workspace-item-list">
            {drafts.map(draft => (
              <div key={draft.id} className="workspace-item">
                <div className="workspace-item-info" onClick={() => navigate(`/create-article?draft=${draft.id}`)} role="button" tabIndex={0} onKeyDown={e => e.key === 'Enter' && navigate(`/create-article?draft=${draft.id}`)}>
                  <div className="workspace-item-title">{draft.title || <em>Untitled</em>}</div>
                  <div className="workspace-item-meta">
                    {draft.community && <span>{draft.community}</span>}
                    <span>{new Date(draft.updated_at).toLocaleString()}</span>
                  </div>
                </div>
                <div className="workspace-item-actions">
                  <button className="workspace-action-btn" onClick={() => navigate(`/create-article?draft=${draft.id}`)}>Edit</button>
                  <button className="workspace-action-btn workspace-delete-btn" onClick={() => handleDelete(draft.id)} disabled={deleting === draft.id}>
                    {deleting === draft.id ? '…' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default ArticleDrafts;
